/**
 * Os ícones, do Lucide.
 *
 * **Eram autorais, e a troca tem motivo.** O conjunto desenhado à mão tinha
 * dezesseis ícones para dezenove destinos, então o menu repetia: a mesma casa
 * servia a Unidades, Locais e Deslocamentos; a mesma etiqueta a Áreas,
 * Disciplinas, Horas-atividade, Jornadas e Aulas coincidentes. Um ícone que se
 * repete não distingue nada — ele só ocupa a coluna e ensina a pessoa a ler
 * pelo texto, que é o contrário do que um ícone existe para fazer.
 *
 * O que se perde: nenhum ícone de biblioteca significa "esquema" ou "Tipo de
 * distribuição" do jeito que um desenho próprio significaria. O que se ganha é
 * maior: cada linha do menu passa a ter uma marca só dela.
 *
 * **O que NÃO muda.** A API deste arquivo é a de antes — `<Icon name="…" />`,
 * com os mesmos nomes de domínio. O resto da interface não sabe que a fonte
 * mudou, e trocar de novo continua sendo um arquivo só. Os nomes são do
 * domínio (`jornadas`, `coincidencias`), nunca do Lucide: `<Icon name="Scale">`
 * amarraria toda a interface à biblioteca.
 *
 * A marca (`Stave`) continua autoral — ela é o produto, não um adorno de
 * lista.
 */

import {
  ChevronDown,
  ClipboardList,
  Banknote,
  AlarmClock,
  Blocks,
  BookOpen,
  CircleQuestionMark,
  Building2,
  CalendarCheck,
  CalendarDays,
  Check,
  CircleAlert,
  Columns2,
  DoorOpen,
  FolderTree,
  GraduationCap,
  Grid3x3,
  Layers,
  Link2,
  Shuffle,
  BadgeCheck,
  SlidersHorizontal,
  Monitor,
  Moon,
  NotebookPen,
  Pin,
  Play,
  Route,
  Sun,
  SunMoon,
  Table2,
  TriangleAlert,
  Upload,
  UserRound,
  Users,
  Waypoints,
  type LucideIcon,
} from 'lucide-react'

/**
 * Nome de domínio → ícone.
 *
 * Um por destino, sem repetição: é o defeito que motivou a troca, e a única
 * regra que este mapa precisa cumprir. Onde a escolha não é óbvia, o
 * comentário diz o que o ícone está tentando dizer.
 */
const ICONES = {
  // --- horário ---------------------------------------------------------
  grade: CalendarDays,
  comparar: Columns2, // duas colunas lado a lado: é o que a tela faz
  custo: Banknote, // dinheiro, e não um gráfico: o que a tela mostra é a folha
  cenarios: Layers, // tentativas empilhadas sobre os mesmos dados

  // --- estrutura -------------------------------------------------------
  unidades: Building2,
  locais: DoorOpen, // a sala, não o prédio — é o que se reserva
  turnos: SunMoon, // manhã, tarde, noite
  esquemas: Grid3x3, // a malha de horários da semana
  deslocamentos: Route, // o trajeto entre unidades

  // --- dados -----------------------------------------------------------
  cursos: GraduationCap,
  turmas: Users,
  areas: FolderTree, // grupo de disciplinas
  disciplinas: BookOpen,
  professores: UserRound,

  // --- grade curricular ------------------------------------------------
  presets: Blocks, // as aulas distribuídas pela semana em blocos
  ofertas: Table2, // a matriz turma × disciplina
  preferencias: CalendarCheck, // quando cada um pode
  horasAtividade: NotebookPen, // o tempo de planejar
  jornadas: AlarmClock, // o descanso mínimo entre jornadas
  coincidencias: Link2, // aulas amarradas ao mesmo horário
  atribuicoes: Shuffle, // quais turmas ficam com quem — o motor decide
  regimes: BadgeCheck, // o vínculo que define o teto de carga
  trilhas: Waypoints, // o itinerário que bifurca

  // --- estado e ação ---------------------------------------------------
  processar: Play,
  importar: Upload,
  formulario: ClipboardList, // o link que o professor preenche
  ajustar: SlidersHorizontal, // o que aparece no menu deste projeto
  ajuda: CircleQuestionMark, // o manual, no rodapé do menu
  recolher: ChevronDown, // o grupo do menu que abre e fecha
  claro: Sun,
  escuro: Moon,
  sistema: Monitor,
  aviso: TriangleAlert,
  pendencia: CircleAlert,
  fixada: Pin,
  ok: Check,
} satisfies Record<string, LucideIcon>

export type IconName = keyof typeof ICONES

export function Icon({ name, size = 16 }: { name: IconName; size?: number }) {
  const Desenho = ICONES[name]
  return (
    <Desenho
      width={size}
      height={size}
      // 1.5 era o traço do conjunto autoral, e continua sendo: o padrão do
      // Lucide é 2, que numa barra de 16px fica pesado ao lado de texto de
      // 13px.
      strokeWidth={1.5}
      aria-hidden
      focusable="false"
    />
  )
}

/**
 * A marca: cinco réguas de uma pauta, a terceira marcada.
 *
 * É o produto inteiro num glifo — linhas paralelas onde eventos são
 * colocados no tempo, e uma delas é onde você está. Continua autoral: uma
 * marca comprada de biblioteca não é marca.
 */
export function Stave({ size = 18 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      aria-hidden
      focusable="false"
      className="wordmark__stave"
    >
      <g stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" opacity=".45">
        <path d="M1.5 3.5h15M1.5 7h15M1.5 14h15M1.5 17.5h15" />
      </g>
      <path d="M1.5 10.5h15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <rect x="10" y="8.25" width="4.5" height="4.5" rx="1" fill="currentColor" />
    </svg>
  )
}
